// CR #189 — Settings › Materials: sweep all receipt PDFs for catalog price
// changes and review them in a pending-approval table. The scan pairs loose
// receipts to their documents first (same matcher as the per-job button),
// then proposes updates; nothing changes a price until approved here.
import { useCallback, useEffect, useState } from 'react';
import apiClient from '@/lib/api/client';

interface Suggestion {
  id: number;
  product_id: number;
  product_name: string;
  product_sku: string | null;
  live_price: string | number | null;
  old_price: string | number | null;
  new_price: string | number;
  vendor: string | null;
  line_desc: string | null;
  job_title: string | null;
  receipt_date: string | null;
  confidence: string | number | null;
}

interface ScanSummary {
  paired: number;
  expenses_scanned: number;
  suggestions_created: number;
  pending: number;
  still_unmatched: { expense_id: number; job_id: number; vendor: unknown; total: number }[];
}

const money = (n: unknown) =>
  n == null ? '—' : `$${Number(n).toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export function CatalogPriceUpdates() {
  const [rows, setRows] = useState<Suggestion[]>([]);
  const [scanning, setScanning] = useState(false);
  const [summary, setSummary] = useState<ScanSummary | null>(null);
  const [busy, setBusy] = useState<number | 'all' | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    apiClient.get('/catalog/price-suggestions', { params: { status: 'pending' } })
      .then(r => setRows(r.data?.data ?? []))
      .catch(() => setRows([]));
  }, []);

  useEffect(() => { load(); }, [load]);

  const scan = async () => {
    setScanning(true);
    setError(null);
    try {
      const r = await apiClient.post('/catalog/price-suggestions/scan');
      setSummary(r.data?.data ?? null);
      load();
    } catch {
      setError('Scan failed — check that receipts have been analyzed.');
    } finally {
      setScanning(false);
    }
  };

  const decide = async (id: number, action: 'approve' | 'reject') => {
    setBusy(id);
    try {
      await apiClient.post(`/catalog/price-suggestions/${id}/${action}`);
      setRows(prev => prev.filter(r => r.id !== id));
    } catch { /* row stays */ } finally {
      setBusy(null);
    }
  };

  const approveAll = async () => {
    if (!window.confirm(`Apply all ${rows.length} pending price updates to the catalog?`)) return;
    setBusy('all');
    try {
      await apiClient.post('/catalog/price-suggestions/approve-all');
      setRows([]);
    } catch { load(); } finally {
      setBusy(null);
    }
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4">
      <div className="flex flex-wrap items-center gap-2 mb-1">
        <span className="material-symbols-outlined text-primary">price_change</span>
        <h3 className="text-sm font-bold text-gray-900 flex-1">Catalog prices from receipts</h3>
        <button
          onClick={scan}
          disabled={scanning}
          className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg bg-primary text-white text-sm font-bold hover:bg-primary/90 disabled:opacity-50"
        >
          <span className={`material-symbols-outlined text-base ${scanning ? 'animate-spin' : ''}`}>
            {scanning ? 'progress_activity' : 'document_scanner'}
          </span>
          {scanning ? 'Scanning…' : 'Scan receipts'}
        </button>
      </div>
      <p className="text-xs text-gray-500 mb-3">
        Pairs every uploaded receipt to its expense, reads the extracted line items, and lists
        price changes below for approval. Approving updates the catalog (with price history) —
        nothing changes until you approve it.
      </p>

      {error && <p className="text-sm text-red-600 mb-2">{error}</p>}
      {summary && (
        <p className="text-xs text-gray-600 mb-3 rounded-lg bg-gray-50 border border-gray-100 px-3 py-2">
          Paired {summary.paired} receipt{summary.paired === 1 ? '' : 's'} to documents ·
          scanned {summary.expenses_scanned} expense{summary.expenses_scanned === 1 ? '' : 's'} ·
          {' '}{summary.suggestions_created} new suggestion{summary.suggestions_created === 1 ? '' : 's'}
          {summary.still_unmatched.length > 0 && (
            <> · {summary.still_unmatched.length} receipt{summary.still_unmatched.length === 1 ? '' : 's'} still
            missing a document (attach them on the job's Expenses tab)</>
          )}
        </p>
      )}

      {rows.length === 0 ? (
        <p className="text-sm text-gray-400 py-4 text-center">No pending price updates. Run a scan to check.</p>
      ) : (
        <>
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-semibold text-gray-600">{rows.length} pending</span>
            <button
              onClick={approveAll}
              disabled={busy !== null}
              className="text-xs font-bold text-primary hover:underline disabled:opacity-50"
            >
              Approve all
            </button>
          </div>
          <div className="overflow-x-auto rounded-lg border border-gray-200">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-gray-500 bg-gray-50 border-b border-gray-100">
                  <th className="px-3 py-2 font-medium">Product</th>
                  <th className="px-3 py-2 font-medium text-right">Current</th>
                  <th className="px-3 py-2 font-medium text-right">Receipt</th>
                  <th className="px-3 py-2 font-medium">Source</th>
                  <th className="px-3 py-2 font-medium text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map(s => {
                  const up = Number(s.new_price) > Number(s.live_price ?? s.old_price ?? 0);
                  return (
                    <tr key={s.id} className="border-b border-gray-50 last:border-0">
                      <td className="px-3 py-2">
                        <p className="font-medium text-gray-900 line-clamp-1">{s.product_name}</p>
                        <p className="text-xs text-gray-400 line-clamp-1">
                          {s.product_sku ? `${s.product_sku} · ` : ''}{s.line_desc}
                        </p>
                      </td>
                      <td className="px-3 py-2 text-right text-gray-500 whitespace-nowrap">{money(s.live_price)}</td>
                      <td className={`px-3 py-2 text-right font-bold whitespace-nowrap ${up ? 'text-red-600' : 'text-green-700'}`}>
                        {money(s.new_price)}
                        <span className="material-symbols-outlined text-sm align-middle ml-0.5">
                          {up ? 'arrow_upward' : 'arrow_downward'}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-xs text-gray-500">
                        {s.vendor || '—'}
                        {s.receipt_date ? ` · ${String(s.receipt_date).slice(0, 10)}` : ''}
                        {s.job_title ? <span className="block text-gray-400 line-clamp-1">{s.job_title}</span> : null}
                      </td>
                      <td className="px-3 py-2 text-right whitespace-nowrap">
                        <button
                          onClick={() => decide(s.id, 'approve')}
                          disabled={busy !== null}
                          className="inline-flex items-center justify-center w-8 h-8 rounded-lg text-green-700 hover:bg-green-50 disabled:opacity-40"
                          title="Approve — update catalog price"
                        >
                          <span className="material-symbols-outlined text-lg">check_circle</span>
                        </button>
                        <button
                          onClick={() => decide(s.id, 'reject')}
                          disabled={busy !== null}
                          className="inline-flex items-center justify-center w-8 h-8 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 disabled:opacity-40"
                          title="Reject"
                        >
                          <span className="material-symbols-outlined text-lg">cancel</span>
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
